import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import axios from "axios";
import "./HospitalCommonPopup.css";
import "./HospitalCreatePopup.css";
import InputContainer from "../../../../components/Util/InputContainer";
import TimePicker from "../../../../components/Util/TimePicker";
import Swal from "sweetalert2";
import { getServerBaseUrl } from "../../../../utils/portConfig";
import { formatPhoneNumber } from "../../../../utils/formatPhoneNumber";

const HospitalPopup = ({ isOpen, onClose, onSave }) => {
  const API_SERVER_URL = `${getServerBaseUrl()}/api/v1/hospital`;
  const token = localStorage.getItem("token");

  // 초기 병원 데이터 설정
  const initialHospitalData = {
    hospitalId: "",
    hospitalName: "",
    hospitalAddress: "",
    hospitalTelNo: "",
    hospitalInfo: "",
    weekdayStartTime: "",
    weekdayEndTime: "",
    nightStartTime: "",
    nightEndTime: "",
    satStartTime: "",
    satEndTime: "",
    lunchStartTime: "",
    lunchEndTime: "",
    restDt: "",
  };

  // 병원 데이터 및 ID 중복 확인 메시지 상태
  const [hospitalData, setHospitalData] = useState({ ...initialHospitalData });
  const [hospitalIdValidationMessage, setHospitalIdValidationMessage] = useState("");

  // 입력값 변경 시 호출되는 함수
  const handleChange = (e) => {
    const { name, value } = e.target;

    // hospitalId의 경우 띄어쓰기 제거 처리
    if (name === "hospitalId") {
      // 공백 제거하여 설정
      return setHospitalData({
        ...hospitalData,
        [name]: value.replace(/\s/g, ""),
      });
    }

    // 전화번호 입력인 경우 포맷팅 적용
    if (name === "hospitalTelNo") {
      setHospitalData({
        ...hospitalData,
        [name]: formatPhoneNumber(value),
      });
    } else {
      setHospitalData({
        ...hospitalData,
        [name]: value,
      });
    }
  };

  // 팝업이 열릴 때 초기화
  useEffect(() => {
    if (!isOpen) {
      setHospitalData({ ...initialHospitalData });
      setHospitalIdValidationMessage("");
    }
  }, [isOpen]);

  // hospitalId 변경 시 중복 확인 메세지 초기화
  useEffect(() => {
    setHospitalIdValidationMessage("");
  }, [hospitalData.hospitalId]);

  // 중복 확인 버튼 클릭 시 호출되는 함수
  const handleCheckDuplicate = async () => {
    const hospitalId = hospitalData.hospitalId.trim();

    if (!hospitalId) {
      Swal.fire("알림", "병원 ID를 입력해주세요.", "warning");
      return;
    }

    const regex = /^[a-zA-Z0-9]+$/;
    if (!regex.test(hospitalId)) {
      Swal.fire("오류", "영문 대소문자와 숫자만 입력 가능합니다.", "warning");
      return;
    }

    if (hospitalId.length < 4 || hospitalId.length > 20) {
      Swal.fire("알림", "병원 ID는 4자 이상 20자 이하로 입력해주세요.", "warning");
      return;
    }

    try {
      const response = await axios.get(`${API_SERVER_URL}/${hospitalId}/check`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        setHospitalIdValidationMessage(response.data ? "중복된 ID입니다." : "사용 가능한 ID입니다.");
      } else {
        Swal.fire("오류", "중복확인 요청에 실패했습니다. 다시 시도해주세요.", "error");
      }
    } catch (error) {
      console.error(error);
    }
  };

  // 저장 버튼 클릭 시 호출되는 함수
  const handleSaveClick = () => {
    if (hospitalData.hospitalId.trim() === "") {
      Swal.fire("알림", "아이디를 입력해주세요.", "warning");
      return;
    }
    if (hospitalIdValidationMessage !== "사용 가능한 ID입니다.") {
      Swal.fire("알림", "아이디 중복확인을 해주세요.", "warning");
      return;
    }
    if (hospitalData.hospitalName.trim() === "") {
      Swal.fire("알림", "병원명을 입력해주세요.", "warning");
      return;
    }
    onSave(hospitalData);
  };

  const handleStartTimeChange = (startTimeInfo) => {
    if (startTimeInfo.startLabels === "평  일") {
      hospitalData.weekdayStartTime = `${startTimeInfo.startTime}시`;
    } else if (startTimeInfo.startLabels === "토요일") {
      hospitalData.satStartTime = `${startTimeInfo.startTime}시`;
    } else if (startTimeInfo.startLabels === "야간진료") {
      hospitalData.nightStartTime = `${startTimeInfo.startTime}시`;
    } else if (startTimeInfo.startLabels === "점심시간") {
      hospitalData.lunchStartTime = `${startTimeInfo.startTime}시`;
    }
  };

  const handleEndTimeChange = (endTimeInfo) => {
    if (endTimeInfo.endLabels === "평  일") {
      hospitalData.weekdayEndTime = `${endTimeInfo.endTime}시`;
    } else if (endTimeInfo.endLabels === "토요일") {
      hospitalData.satEndTime = `${endTimeInfo.endTime}시`;
    } else if (endTimeInfo.endLabels === "야간진료") {
      hospitalData.nightEndTime = `${endTimeInfo.endTime}시`;
    } else if (endTimeInfo.endLabels === "점심시간") {
      hospitalData.lunchEndTime = `${endTimeInfo.endTime}시`;
    }
  };
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Hospital Popup"
      style={{
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1000,
        },
        content: {
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          border: "1px solid #ccc",
          background: "white",
          borderRadius: "4px",
          width: "400px",
          height: "640px",
        },
      }}
    >
      <div className="hospital-modal-content">
        <div className="modal-header">
          <h2>병원 생성</h2>
        </div>
        <InputContainer
          label="병원 ID"
          name="hospitalId"
          value={hospitalData.hospitalId}
          onChange={handleChange}
          validationMessage={hospitalIdValidationMessage}
          validationColor={hospitalIdValidationMessage === "중복된 ID입니다." ? "red" : "green"}
          buttonLabel="중복 확인"
          onButtonClick={handleCheckDuplicate}
        />
        <InputContainer label="병원명" name="hospitalName" value={hospitalData.hospitalName} onChange={handleChange} />
        <InputContainer label="병원 주소" name="hospitalAddress" value={hospitalData.hospitalAddress} onChange={handleChange} />
        <InputContainer label="병원 전화번호" name="hospitalTelNo" value={hospitalData.hospitalTelNo} onChange={handleChange} />
        <InputContainer label="병원 소개" name="hospitalInfo" value={hospitalData.hospitalInfo} onChange={handleChange} />
        <div className="input-container" style={{ marginBottom: "10px" }}>
          <div className="input-row" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <label className="hospital-time-head" htmlFor="hospitalId">
              병원 시간
            </label>
            <div className="hospital-table-header">
              <div className="hospital-gubun">구분</div>
              <div className="hospital-content">내용</div>
            </div>
            <TimePicker
              labels={"평  일"}
              startTime={hospitalData.weekdayStartTime === undefined || hospitalData.weekdayStartTime === "" ? "" : hospitalData.weekdayStartTime.replace("시", "")}
              endTime={hospitalData.weekdayEndTime === undefined || hospitalData.weekdayEndTime === "" ? "" : hospitalData.weekdayEndTime.replace("시", "")}
              onStartTimeChange={handleStartTimeChange}
              onEndTimeChange={handleEndTimeChange}
            />
            <TimePicker
              labels={"토요일"}
              startTime={hospitalData.satStartTime === undefined || hospitalData.satStartTime === "" ? "" : hospitalData.satStartTime.replace("시", "")}
              endTime={hospitalData.satEndTime === undefined || hospitalData.satEndTime === "" ? "" : hospitalData.satEndTime.replace("시", "")}
              onStartTimeChange={handleStartTimeChange}
              onEndTimeChange={handleEndTimeChange}
            />
            <TimePicker
              labels={"야간진료"}
              startTime={hospitalData.nightStartTime === undefined || hospitalData.nightStartTime === "" ? "" : hospitalData.nightStartTime.replace("시", "")}
              endTime={hospitalData.nightEndTime === undefined || hospitalData.nightEndTime === "" ? "" : hospitalData.nightEndTime.replace("시", "")}
              onStartTimeChange={handleStartTimeChange}
              onEndTimeChange={handleEndTimeChange}
            />
            <TimePicker
              labels={"점심시간"}
              startTime={hospitalData.lunchStartTime === undefined || hospitalData.lunchStartTime === "" ? "" : hospitalData.lunchStartTime.replace("시", "")}
              endTime={hospitalData.lunchEndTime === undefined || hospitalData.lunchEndTime === "" ? "" : hospitalData.lunchEndTime.replace("시", "")}
              onStartTimeChange={handleStartTimeChange}
              onEndTimeChange={handleEndTimeChange}
            />
            <div className="rest-time-container">
              <div className="hospital-time">휴 진</div>
              <div className="hospital-time-info">
                <input type="text" name="restDt" value={hospitalData.restDt} onChange={handleChange} placeholder="휴진일을 입력해주세요." />
              </div>
            </div>
          </div>
        </div>
        <div className="button-container">
          <button className="dashboard-content-button popup-button save-button" onClick={handleSaveClick}>
            저장
          </button>
          <button className="dashboard-content-button red-button popup-button cancel-button" onClick={onClose}>
            취소
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default HospitalPopup;
